'use client';

import { createContext, useContext, useState, useEffect } from 'react';

// Create language context
const LanguageContext = createContext();

// Available languages
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
];

// Translations
export const translations = {
  en: {
    // Navigation
    aboutUs: 'About Us',
    buy: 'Buy',
    rent: 'Rent',
    offPlan: 'Off Plan',
    newsArticles: 'News / Articles',
    blog: 'Blog',
    agent: 'Agent',
    contactUs: 'Contact Us',

    // Hero Section
    heroTitle: 'Find Your Dream Property in Dubai',
    heroSubtitle:
      'Discover luxury properties in Dubai\'s most prestigious locations',

    // Property Search
    searchPlaceholder: 'City, community or building',
    propertyType: 'Property type',
    bedsAndBaths: 'Beds & Baths',
    search: 'Search',
    noResultsFound: 'No results found',
    tryDifferentSearch: 'Try a different search term or browse our properties',
    clearSearch: 'Clear search',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    reset: 'Reset',
    ok: 'OK',
    select: 'Select',
    beds: 'Beds',
    baths: 'Baths',
    commercial: 'Commercial',

    // Categories
    exploreServices: 'Explore Our Services',
    buyProperties: 'Buy Properties',
    buyDescription:
      'Find your perfect home from our curated selection of properties for sale.',
    rentProperties: 'Rent Properties',
    rentDescription:
      'Explore premium rental properties in Dubai\'s most sought-after locations.',
    offPlanProjects: 'Off-Plan Projects',
    offPlanDescription:
      'Invest in Dubai\'s future with our exclusive off-plan developments.',
    explore: 'Explore',

    // Featured Properties
    featuredProperties: 'Featured Properties',
    featuredDescription:
      'Discover our handpicked selection of Dubai\'s most exclusive properties',
    viewDetails: 'View Details',
    viewAllProperties: 'View All Properties',

    // Agents Section
    meetOurAgents: 'Meet Our Expert Agents',
    realEstateAgents: 'Real Estate Agents',
    agentsExperience:
      'Our professional agents have years of experience in the Dubai real estate market and are ready to help you find your dream property. With deep local knowledge and expertise in various property segments, they\'ll guide you through every step of your real estate journey.',
    buySellInvest:
      'Whether you\'re looking to buy, sell, or invest in Dubai\'s dynamic property market, our agents are committed to providing personalized service tailored to your specific needs and preferences',
    yearsExperience: 'Years Experience',
    propertiesSold: 'Properties Sold',
    contactAgent: 'Contact Agent',
    viewProfile: 'View Profile',
    meetAllAgents: 'Meet All Agents',
    showAllAgents: 'Show All',
    specialization: 'Specialization',
    languages: 'Languages',
    contactNow: 'Contact Now',

    // Top Sales Section
    topSalesAgents: 'Top Sales Agents',
    topSalesDescription: 'Recognizing our highest performing agents',
    filterByPeriod: 'Filter by Period',
    month: 'Month',
    quarter: 'Quarter',
    year: 'Year',
    salesVolume: 'Sales Volume',
    transactions: 'Transactions',
    ranking: 'Ranking',

    // Awards & Recognition
    awardsRecognition: 'Our Awards & Recognition',
    awardsDescription:
      'We\'re proud to be recognized for our excellence in real estate services and customer satisfaction.',
    scrollToExplore: 'Scroll to explore all awards',
    realEstateExcellence: 'Excellence in Real Estate',

    // Partner Developers
    partnerDevelopers: 'Our Partner Developers',
    partnerDevelopersDescription:
      'We work with Dubai\'s most prestigious developers to bring you exceptional properties and investment opportunities.',
    visitWebsite: 'Visit Website',

    // Why Choose Us
    whyChooseUs: 'Why Choose X Space',
    whyChooseUsDescription:
      'We\'re committed to providing exceptional service and tailored solutions for all your real estate needs in Dubai.',
    marketExpertise: 'Market Expertise',
    marketExpertiseDescription:
      'Our team has in-depth knowledge of the Dubai real estate market, ensuring you make informed investment decisions.',
    trustedAdvisors: 'Trusted Advisors',
    trustedAdvisorsDescription:
      'With thousands of successful transactions, we\'ve built a reputation for honesty, integrity, and customer satisfaction.',
    timeEfficiency: 'Time Efficiency',
    timeEfficiencyDescription:
      'We value your time and streamline the property search process to find your ideal property quickly.',
    awardWinningService: 'Award-Winning Service',
    awardWinningServiceDescription:
      'Recognized for excellence in real estate services, we consistently exceed client expectations.',

    // News & Insights
    newsInsights: 'News & Insights',
    newsInsightsDescription:
      'Stay updated with the latest news, market trends, and insights from the Dubai real estate market.',
    latestNews: 'Latest News',
    readMore: 'Read More',
    featuredBlogPosts: 'Featured Blog Posts',
    viewAll: 'View All',
    browseAllArticles: 'Browse All Articles',

    // FAQ
    faq: 'Frequently Asked Questions',
    faqDescription:
      'Find answers to common questions about buying, selling, and investing in Dubai real estate.',
    cantFindAnswer: 'Can\'t find the answer you\'re looking for?',
    contactOurTeam: 'Contact Our Team',

    // Call to Action
    readyToFindProperty: 'Ready to Find Your Dream Property?',
    readyToFindPropertyDescription:
      'Contact our expert team today and let us help you find the perfect Dubai property that matches your requirements and budget.',
    contactUsNow: 'Contact Us Now',
    viewMoreProperties: 'View More Properties',

    // Testimonials
    clientTestimonials: 'What Our Clients Say',
    clientTestimonialsDescription:
      'Read what our satisfied clients have to say about their experience working with X Space Real Estate.',

    // Time periods
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December',
    q1: 'Q1',
    q2: 'Q2',
    q3: 'Q3',
    q4: 'Q4',

    // Footer
    footerDescription:
      'Your trusted partner in Dubai real estate, offering exceptional properties and personalized service.',
    company: 'Company',
    properties: 'Properties',
    contactInfo: 'Contact Info',
    allRightsReserved: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    cookiesPolicy: 'Cookies Policy',
    careers: 'Careers',
    services: 'Services',
    newProjects: 'New Projects',
    workingHours: 'Mon - Sat, 9:30 - 6:30',
    location: 'Dubai, UAE',

    //About Us
    atXSpace_1:
      'At X-Space Real Estate Broker, we are committed to making space accessible and profitable for our clients. Join us as we expand the boundaries of real estate and turn the dream of space colonization into a reality.',
    atXSpace_2:
      'Explore the cosmos with us and secure your place in the future of humanity.',
    learnMore: 'Learn More',
    yearsExperience: 'Years Experience',
    companyName: 'X - SPACE REAL ESTATE',
    ourVisionaryLeaders: 'Our Visionary Leaders',
    welcometoXSpace:
      'Welcome to X-Space Real Estate Broker, your premier destination for buying, selling, and investing in the final frontier. As a pioneering space real estate brokerage, we specialize in connecting visionaries with prime extraterrestrial properties.',
    lookingToAcquire:
      'Whether you\'re looking to acquire land on the Moon, develop commercial ventures on Mars, or invest in orbital habitats, our expert team is here to guide you through every step of the process.',
    company_detail_1:
      'formerly known as Good Reputation Real Estate Broker is a leading real estate brokerage and property management company headquartered in Dubai. Since our inception in 2017, we have built a strong reputation for providing excellent service and exceptional customer experiences. Our dedicated team of real estate experts is committed to finding you the perfect property to meet your needs.',
    company_detail_2:
      'Annually, our team successfully executes real estate transactions totaling more than AED 200 billion. By forging close partnerships with developers in the U.A.E, we ensure that our clients have access to the best options and services customized to their specific requirements. Our approach is centered around market knowledge, thorough research, and a commitment to providing tailored advice to our clients.',
    company_detail_3:
      'At X-Space Real Estate, we uphold the values of honesty, trustworthiness, enthusiasm, thoughtfulness, sincerity, and responsibility. We operate in strict compliance with U.A.E laws and regulations to ensure the highest standards of professionalism and integrity.',
  },
  ar: {
    // Navigation
    aboutUs: 'من نحن',
    buy: 'شراء',
    rent: 'إيجار',
    offPlan: 'خارج الخطة',
    newsArticles: 'الأخبار / المقالات',
    blog: 'المدونة',
    agent: 'الوكيل',
    contactUs: 'اتصل بنا',

    // Hero Section
    heroTitle: 'ابحث عن عقارك المثالي في دبي',
    heroSubtitle: 'اكتشف العقارات الفاخرة في أرقى مواقع دبي',

    // Property Search
    searchPlaceholder: 'مدينة، مجتمع أو مبنى',
    propertyType: 'نوع العقار',
    bedsAndBaths: 'الغرف والحمامات',
    search: 'بحث',
    noResultsFound: 'لم يتم العثور على نتائج',
    tryDifferentSearch: 'حاول البحث بمصطلح مختلف أو تصفح عقاراتنا',
    clearSearch: 'مسح البحث',

    // Categories
    exploreServices: 'استكشف خدماتنا',
    buyProperties: 'شراء العقارات',
    buyDescription:
      'اعثر على منزلك المثالي من بين مجموعة مختارة من العقارات المعروضة للبيع.',
    rentProperties: 'استئجار العقارات',
    rentDescription: 'استكشف العقارات الفاخرة للإيجار في أكثر مواقع دبي طلبًا.',
    offPlanProjects: 'مشاريع خارج الخطة',
    offPlanDescription: 'استثمر في مستقبل دبي مع مشاريعنا الحصرية خارج الخطة.',
    explore: 'استكشاف',

    // Featured Properties
    featuredProperties: 'العقارات المميزة',
    featuredDescription:
      'اكتشف مجموعة مختارة بعناية من أكثر العقارات الحصرية في دبي',
    viewDetails: 'عرض التفاصيل',
    viewAllProperties: 'عرض جميع العقارات',

    // Agents Section
    meetOurAgents: 'تعرف على وكلائنا الخبراء',
    realEstateAgents: 'وكلاء العقارات',
    agentsExperience:
      'يتمتع وكلاؤنا المحترفون بسنوات من الخبرة في سوق العقارات في دبي، وهم مستعدون لمساعدتك في العثور على عقارك المثالي. بفضل معرفتهم العميقة بالسوق المحلي وخبرتهم في مختلف القطاعات العقارية، سيرشدونك خلال كل خطوة في رحلتك العقارية.',
    buySellInvest:
      'سواء كنت تبحث عن شراء أو بيع أو الاستثمار في سوق العقارات الديناميكي في دبي، يلتزم وكلاؤنا بتقديم خدمة شخصية مصممة خصيصًا لتلبية احتياجاتك وتفضيلاتك الخاصة.',
    yearsExperience: 'سنوات الخبرة',
    propertiesSold: 'العقارات المباعة',
    contactAgent: 'اتصل بالوكيل',
    viewProfile: 'عرض الملف الشخصي',
    meetAllAgents: 'التعرف على جميع الوكلاء',
    showAllAgents: 'عرض الكل',
    specialization: 'التخصص',
    languages: 'اللغات',
    contactNow: 'اتصل الآن',

    // Top Sales Section
    topSalesAgents: 'أفضل وكلاء المبيعات',
    topSalesDescription: 'تكريم وكلائنا الأعلى أداءً',
    filterByPeriod: 'تصفية حسب الفترة',
    month: 'الشهر',
    quarter: 'الربع',
    year: 'السنة',
    salesVolume: 'حجم المبيعات',
    transactions: 'المعاملات',
    ranking: 'التصنيف',

    // Awards & Recognition
    awardsRecognition: 'جوائزنا وتقديراتنا',
    awardsDescription:
      'نفخر بالاعتراف بنا لتميزنا في خدمات العقارات ورضا العملاء.',
    scrollToExplore: 'قم بالتمرير لاستكشاف جميع الجوائز',
    realEstateExcellence: 'التميز في العقارات',

    // Partner Developers
    partnerDevelopers: 'المطورون الشركاء',
    partnerDevelopersDescription:
      'نعمل مع أرقى المطورين في دبي لنقدم لك عقارات وفرص استثمارية متميزة.',
    visitWebsite: 'زيارة الموقع',

    // Why Choose Us
    whyChooseUs: 'لماذا تختار X Space',
    whyChooseUsDescription:
      'نحن ملتزمون بتقديم خدمات استثنائية وحلول مخصصة لجميع احتياجاتك العقارية في دبي.',
    marketExpertise: 'الخبرة في السوق',
    marketExpertiseDescription:
      'فريقنا لديه معرفة متعمقة بسوق العقارات في دبي، مما يضمن لك اتخاذ قرارات استثمارية مستنيرة.',
    trustedAdvisors: 'مستشارون موثوقون',
    trustedAdvisorsDescription:
      'مع آلاف المعاملات الناجحة، بنينا سمعة قائمة على النزاهة والشفافية ورضا العملاء.',
    timeEfficiency: 'الكفاءة في الوقت',
    timeEfficiencyDescription:
      'نقدر وقتك ونقوم بتبسيط عملية البحث عن العقار لإيجاد العقار المثالي بسرعة.',
    awardWinningService: 'خدمة حائزة على جوائز',
    awardWinningServiceDescription:
      'تم الاعتراف بنا للتميز في خدمات العقارات، ونسعى دائمًا لتجاوز توقعات العملاء.',

    // News & Insights
    newsInsights: 'الأخبار والرؤى',
    newsInsightsDescription:
      'ابقَ على اطلاع بآخر الأخبار واتجاهات السوق والرؤى حول سوق العقارات في دبي.',
    latestNews: 'آخر الأخبار',
    readMore: 'اقرأ المزيد',
    featuredBlogPosts: 'المقالات المميزة',
    viewAll: 'عرض الكل',
    browseAllArticles: 'تصفح جميع المقالات',

    // FAQ
    faq: 'الأسئلة الشائعة',
    faqDescription:
      'ابحث عن إجابات للأسئلة الشائعة حول شراء وبيع والاستثمار في العقارات في دبي.',
    cantFindAnswer: 'لا يمكنك العثور على الإجابة التي تبحث عنها؟',
    contactOurTeam: 'اتصل بفريقنا',

    // Call to Action
    readyToFindProperty: 'هل أنت مستعد للعثور على عقارك المثالي؟',
    readyToFindPropertyDescription:
      'اتصل بفريقنا الخبير اليوم ودعنا نساعدك في العثور على العقار المثالي في دبي الذي يناسب متطلباتك وميزانيتك.',
    contactUsNow: 'اتصل بنا الآن',
    viewMoreProperties: 'عرض المزيد من العقارات',

    // Testimonials
    clientTestimonials: 'آراء عملائنا',
    clientTestimonialsDescription:
      'اقرأ ما يقوله عملاؤنا الراضون عن تجربتهم مع X Space Real Estate.',

    // Property Details
    bedrooms: 'غرف النوم',
    bathrooms: 'الحمامات',
    reset: 'إعادة تعيين',
    ok: 'موافق',
    select: 'اختيار',
    beds: 'غرف',
    baths: 'حمامات',
    commercial: 'تجاري',

    // Time periods
    january: 'يناير',
    february: 'فبراير',
    march: 'مارس',
    april: 'أبريل',
    may: 'مايو',
    june: 'يونيو',
    july: 'يوليو',
    august: 'أغسطس',
    september: 'سبتمبر',
    october: 'أكتوبر',
    november: 'نوفمبر',
    december: 'ديسمبر',
    q1: 'الربع الأول',
    q2: 'الربع الثاني',
    q3: 'الربع الثالث',
    q4: 'الربع الرابع',

    // Footer
    footerDescription:
      'شريكك الموثوق في عقارات دبي، حيث نقدم عقارات استثنائية وخدمة متميزة.',
    company: 'الشركة',
    properties: 'العقارات',
    contactInfo: 'معلومات الاتصال',
    allRightsReserved: 'جميع الحقوق محفوظة.',
    privacyPolicy: 'سياسة الخصوصية',
    termsOfService: 'شروط الخدمة',
    cookiesPolicy: 'سياسة ملفات تعريف الارتباط',
    careers: 'الوظائف',
    services: 'الخدمات',
    newProjects: 'المشاريع الجديدة',
    workingHours: 'الإثنين - السبت، 9:30 - 6:30',
    location: 'دبي، الإمارات العربية المتحدة',
  },
  zh: {
    // Navigation
    aboutUs: '关于我们',
    buy: '购买',
    rent: '租赁',
    offPlan: '期房',
    newsArticles: '新闻 / 文章',
    blog: '博客',
    agent: '经纪人',
    contactUs: '联系我们',

    // Hero Section
    heroTitle: '在迪拜找到您的梦想房产',
    heroSubtitle: '探索迪拜最负盛名地区的豪华房产',

    // Property Search
    searchPlaceholder: '城市、社区或建筑',
    propertyType: '房产类型',
    bedsAndBaths: '卧室 & 卫浴',
    search: '搜索',
    noResultsFound: '未找到结果',
    tryDifferentSearch: '尝试不同的搜索词或浏览我们的房产',
    clearSearch: '清除搜索',

    // Categories
    exploreServices: '探索我们的服务',
    buyProperties: '购买房产',
    buyDescription: '从我们精心挑选的房产中找到您的理想家园。',
    rentProperties: '租赁房产',
    rentDescription: '探索迪拜最受欢迎地区的高端出租房产。',
    offPlanProjects: '期房项目',
    offPlanDescription: '通过我们的独家期房项目投资迪拜的未来。',
    explore: '探索',

    // Featured Properties
    featuredProperties: '精选房产',
    featuredDescription: '发现我们精心挑选的迪拜最独特房产',
    viewDetails: '查看详情',
    viewAllProperties: '查看所有房产',

    // Agents Section
    meetOurAgents: '认识我们的专业经纪人',
    realEstateAgents: '房地产经纪人',
    agentsExperience:
      '我们的专业经纪人在迪拜房地产市场拥有多年的经验，随时准备帮助您找到梦想中的房产。凭借对本地市场的深入了解和各类房产的专业知识，他们将引导您完成房地产旅程的每一步。',
    buySellInvest:
      '无论您是想在迪拜充满活力的房地产市场中买房、卖房，还是投资，我们的经纪人都致力于为您提供量身定制的个性化服务，以满足您的特定需求和偏好。',
    yearsExperience: '年经验',
    propertiesSold: '已售房产',
    contactAgent: '联系经纪人',
    viewProfile: '查看资料',
    meetAllAgents: '查看所有经纪人',
    showAllAgents: '显示全部',
    specialization: '专业领域',
    languages: '语言',
    contactNow: '立即联系',

    // Top Sales Section
    topSalesAgents: '顶级销售经纪人',
    topSalesDescription: '表彰我们表现最优秀的经纪人',
    filterByPeriod: '按时间筛选',
    month: '月',
    quarter: '季度',
    year: '年',
    salesVolume: '销售额',
    transactions: '交易量',
    ranking: '排名',

    // Awards & Recognition
    awardsRecognition: '我们的奖项与认可',
    awardsDescription: '我们为在房地产服务和客户满意度方面的卓越表现感到自豪。',
    scrollToExplore: '滚动浏览所有奖项',
    realEstateExcellence: '房地产卓越',

    // Partner Developers
    partnerDevelopers: '合作开发商',
    partnerDevelopersDescription:
      '我们与迪拜最知名的开发商合作，为您带来卓越的房产和投资机会。',
    visitWebsite: '访问网站',

    // Why Choose Us
    whyChooseUs: '为什么选择 X Space',
    whyChooseUsDescription:
      '我们致力于为您提供卓越的服务和量身定制的房地产解决方案。',
    marketExpertise: '市场专业知识',
    marketExpertiseDescription:
      '我们的团队深入了解迪拜房地产市场，确保您做出明智的投资决策。',
    trustedAdvisors: '值得信赖的顾问',
    trustedAdvisorsDescription:
      '凭借数千笔成功交易，我们建立了诚信、透明和客户满意度的良好声誉。',
    timeEfficiency: '高效服务',
    timeEfficiencyDescription:
      '我们珍惜您的时间，并简化房产搜索流程，让您快速找到理想房产。',
    awardWinningService: '屡获殊荣的服务',
    awardWinningServiceDescription:
      '凭借卓越的房地产服务，我们始终超越客户的期望。',

    // News & Insights
    newsInsights: '新闻与洞察',
    newsInsightsDescription:
      '随时了解迪拜房地产市场的最新新闻、趋势和行业洞察。',
    latestNews: '最新新闻',
    readMore: '阅读更多',
    featuredBlogPosts: '精选博客文章',
    viewAll: '查看全部',
    browseAllArticles: '浏览所有文章',

    // FAQ
    faq: '常见问题',
    faqDescription: '查找有关在迪拜买卖和投资房地产的常见问题解答。',
    cantFindAnswer: '找不到您要的答案？',
    contactOurTeam: '联系我们的团队',

    // Call to Action
    readyToFindProperty: '准备好找到您的梦想房产了吗？',
    readyToFindPropertyDescription:
      '立即联系我们的专业团队，让我们帮助您找到符合您需求和预算的完美迪拜房产。',
    contactUsNow: '立即联系我们',
    viewMoreProperties: '查看更多房产',

    // Testimonials
    clientTestimonials: '客户评价',
    clientTestimonialsDescription:
      '阅读我们满意客户的评价，了解他们与 X Space Real Estate 的合作体验。',

    // Property Details
    bedrooms: '卧室',
    bathrooms: '浴室',
    reset: '重置',
    ok: '确定',
    select: '选择',
    beds: '卧室',
    baths: '浴室',
    commercial: '商业',

    // Time periods
    january: '一月',
    february: '二月',
    march: '三月',
    april: '四月',
    may: '五月',
    june: '六月',
    july: '七月',
    august: '八月',
    september: '九月',
    october: '十月',
    november: '十一月',
    december: '十二月',
    q1: '第一季度',
    q2: '第二季度',
    q3: '第三季度',
    q4: '第四季度',

    // Footer
    footerDescription:
      '您值得信赖的迪拜房地产合作伙伴，提供卓越的房产和个性化服务。',
    company: '公司',
    properties: '房产',
    contactInfo: '联系方式',
    allRightsReserved: '版权所有。',
    privacyPolicy: '隐私政策',
    termsOfService: '服务条款',
    cookiesPolicy: 'Cookie 政策',
    careers: '职业机会',
    services: '服务',
    newProjects: '新项目',
    workingHours: '周一至周六，9:30 - 18:30',
    location: '迪拜，阿联酋',
  },
  fr: {
    // Navigation
    aboutUs: 'À propos de nous',
    buy: 'Acheter',
    rent: 'Louer',
    offPlan: 'Sur plan',
    newsArticles: 'Actualités / Articles',
    blog: 'Blog',
    agent: 'Agent',
    contactUs: 'Contactez-nous',

    // Hero Section
    heroTitle: 'Trouvez la propriété de vos rêves à Dubaï',
    heroSubtitle:
      'Découvrez des propriétés de luxe dans les endroits les plus prestigieux de Dubaï',

    // Property Search
    searchPlaceholder: 'Ville, communauté ou immeuble',
    propertyType: 'Type de propriété',
    bedsAndBaths: 'Chambres & Salles de bain',
    search: 'Rechercher',
    noResultsFound: 'Aucun résultat trouvé',
    tryDifferentSearch:
      'Essayez un autre terme de recherche ou explorez nos propriétés',
    clearSearch: 'Effacer la recherche',

    // Categories
    exploreServices: 'Découvrez nos services',
    buyProperties: 'Acheter des propriétés',
    buyDescription:
      'Trouvez votre maison idéale parmi notre sélection de propriétés à vendre.',
    rentProperties: 'Louer des propriétés',
    rentDescription:
      'Découvrez des locations haut de gamme dans les endroits les plus prisés de Dubaï.',
    offPlanProjects: 'Projets sur plan',
    offPlanDescription:
      'Investissez dans l\'avenir de Dubaï avec nos développements exclusifs sur plan.',
    explore: 'Explorer',

    // Featured Properties
    featuredProperties: 'Propriétés en vedette',
    featuredDescription:
      'Découvrez notre sélection exclusive des meilleures propriétés de Dubaï',
    viewDetails: 'Voir les détails',
    viewAllProperties: 'Voir toutes les propriétés',

    // Agents Section
    meetOurAgents: 'Rencontrez Nos Agents Experts',
    realEstateAgents: 'Agents Immobiliers',
    agentsExperience:
      'Nos agents professionnels possèdent des années d\'expérience sur le marché immobilier de Dubaï et sont prêts à vous aider à trouver la propriété de vos rêves. Grâce à leur connaissance approfondie du marché local et leur expertise dans différents segments immobiliers, ils vous guideront à chaque étape de votre parcours immobilier.',
    buySellInvest:
      'Que vous souhaitiez acheter, vendre ou investir sur le marché immobilier dynamique de Dubaï, nos agents s\'engagent à vous offrir un service personnalisé adapté à vos besoins et préférences spécifiques.',
    yearsExperience: 'Années d\'Expérience',
    propertiesSold: 'Biens Vendus',
    contactAgent: 'Contacter l\'Agent',
    viewProfile: 'Voir le Profil',
    meetAllAgents: 'Voir Tous les Agents',
    showAllAgents: 'Voir Tout',
    specialization: 'Spécialisation',
    languages: 'Langues',
    contactNow: 'Contacter Maintenant',

    // Top Sales Section
    topSalesAgents: 'Meilleurs agents de vente',
    topSalesDescription: 'Reconnaissance de nos agents les plus performants',
    filterByPeriod: 'Filtrer par période',
    month: 'Mois',
    quarter: 'Trimestre',
    year: 'Année',
    salesVolume: 'Volume des ventes',
    transactions: 'Transactions',
    ranking: 'Classement',

    // Awards & Recognition
    awardsRecognition: 'Nos récompenses et reconnaissances',
    awardsDescription:
      'Nous sommes fiers d’être reconnus pour notre excellence en immobilier et notre satisfaction client.',
    scrollToExplore: 'Faites défiler pour explorer toutes les récompenses',
    realEstateExcellence: 'Excellence en Immobilier',

    // Partner Developers
    partnerDevelopers: 'Nos partenaires promoteurs',
    partnerDevelopersDescription:
      'Nous travaillons avec les promoteurs les plus prestigieux de Dubaï pour vous offrir des propriétés et des opportunités d’investissement exceptionnelles.',
    visitWebsite: 'Visiter le site web',

    // Why Choose Us
    whyChooseUs: 'Pourquoi choisir X Space',
    whyChooseUsDescription:
      'Nous nous engageons à offrir un service exceptionnel et des solutions sur mesure pour tous vos besoins immobiliers à Dubaï.',
    marketExpertise: 'Expertise du marché',
    marketExpertiseDescription:
      'Notre équipe possède une connaissance approfondie du marché immobilier de Dubaï, vous aidant à prendre des décisions d’investissement éclairées.',
    trustedAdvisors: 'Conseillers de confiance',
    trustedAdvisorsDescription:
      'Avec des milliers de transactions réussies, nous avons bâti une réputation d’honnêteté, d’intégrité et de satisfaction client.',
    timeEfficiency: 'Efficacité temporelle',
    timeEfficiencyDescription:
      'Nous valorisons votre temps et optimisons le processus de recherche pour trouver rapidement votre propriété idéale.',
    awardWinningService: 'Service primé',
    awardWinningServiceDescription:
      'Reconnus pour notre excellence en services immobiliers, nous dépassons systématiquement les attentes de nos clients.',

    // News & Insights
    newsInsights: 'Actualités et analyses',
    newsInsightsDescription:
      'Restez informé des dernières nouvelles, tendances du marché et analyses du secteur immobilier à Dubaï.',
    latestNews: 'Dernières nouvelles',
    readMore: 'Lire la suite',
    featuredBlogPosts: 'Articles de blog en vedette',
    viewAll: 'Voir tout',
    browseAllArticles: 'Parcourir tous les articles',

    // FAQ
    faq: 'Foire aux questions',
    faqDescription:
      'Trouvez des réponses aux questions courantes sur l\'achat, la vente et l’investissement immobilier à Dubaï.',
    cantFindAnswer: 'Vous ne trouvez pas la réponse que vous cherchez?',
    contactOurTeam: 'Contactez notre équipe',

    // Call to Action
    readyToFindProperty: 'Prêt à trouver la propriété de vos rêves?',
    readyToFindPropertyDescription:
      'Contactez notre équipe d’experts dès aujourd’hui et laissez-nous vous aider à trouver la propriété idéale à Dubaï correspondant à vos besoins et à votre budget.',
    contactUsNow: 'Contactez-nous maintenant',
    viewMoreProperties: 'Voir plus de propriétés',

    // Testimonials
    clientTestimonials: 'Ce que disent nos clients',
    clientTestimonialsDescription:
      'Lisez les témoignages de nos clients satisfaits à propos de leur expérience avec X Space Real Estate.',

    // Property Details
    bedrooms: 'Chambres',
    bathrooms: 'Salles de bain',
    reset: 'Réinitialiser',
    ok: 'OK',
    select: 'Sélectionner',
    beds: 'Lits',
    baths: 'Bains',
    commercial: 'Commercial',

    // Time periods
    january: 'Janvier',
    february: 'Février',
    march: 'Mars',
    april: 'Avril',
    may: 'Mai',
    june: 'Juin',
    july: 'Juillet',
    august: 'Août',
    september: 'Septembre',
    october: 'Octobre',
    november: 'Novembre',
    december: 'Décembre',
    q1: 'T1',
    q2: 'T2',
    q3: 'T3',
    q4: 'T4',

    // Footer
    footerDescription:
      'Votre partenaire de confiance dans l\'immobilier à Dubaï, offrant des propriétés exceptionnelles et un service personnalisé.',
    company: 'Entreprise',
    properties: 'Propriétés',
    contactInfo: 'Infos de contact',
    allRightsReserved: 'Tous droits réservés.',
    privacyPolicy: 'Politique de confidentialité',
    termsOfService: 'Conditions d\'utilisation',
    cookiesPolicy: 'Politique de cookies',
    careers: 'Carrières',
    services: 'Services',
    newProjects: 'Nouveaux projets',
    workingHours: 'Lun - Sam, 9:30 - 18:30',
    location: 'Dubaï, EAU',
  },
};

export function LanguageProvider({ children }) {
  // Get initial language from localStorage or default to English
  const [language, setLanguage] = useState('en');

  // Set document direction based on language
  useEffect(() => {
    const selectedLang = languages.find((lang) => lang.code === language);
    if (selectedLang) {
      document.documentElement.dir = selectedLang.dir;
      document.documentElement.lang = selectedLang.code;
    }

    // Store language preference
    localStorage.setItem('language', language);
  }, [language]);

  // Load saved language on initial render
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Get translation function
  const t = (key) => {
    const keys = key.split('.');
    let translation = translations[language];

    for (const k of keys) {
      if (translation && translation[k]) {
        translation = translation[k];
      } else {
        // Fallback to English if translation not found
        let fallback = translations['en'];
        for (const fk of keys) {
          if (fallback && fallback[fk]) {
            fallback = fallback[fk];
          } else {
            return key; // Return the key if no translation found
          }
        }
        return fallback;
      }
    }

    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Custom hook to use the language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
